import { booleanParam, numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { judgeRange, missingRequirementReason, notChecked, plainResult } from './support.js';

const direction = (offset: number): string => (offset > 0 ? 'вправо' : 'влево');

/**
 * Centring check.
 *
 * Emits the head box, the face centre point and a horizontal shift line, which
 * together are what makes the centring problem visible on the photo itself.
 */
export const facePositionCheck: ValidationCheck = {
  id: 'face_position',
  title: 'Лицо по центру',
  description: 'Проверяет, что голова расположена по центру кадра.',
  kind: 'geometry',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, facePositionCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, facePositionCheck, missing ?? 'Геометрия недоступна.', ctx.rule.severity);

    const maxHorizontal = numberParam(ctx.rule.params, 'maxHorizontalOffset', 0.06);
    const maxVertical = numberParam(ctx.rule.params, 'maxVerticalOffset', 0.12);
    /**
     * Vertical placement is governed by the eye-line and head-size rules, so it
     * stays informational unless a document explicitly asks for a vertical check.
     */
    const checkVertical = booleanParam(ctx.rule.params, 'checkVertical', false);
    const confidence = geometry.faceBoxConfidence.value;
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.3);

    const horizontal = Math.abs(geometry.horizontalCenterOffset);
    const vertical = Math.abs(geometry.verticalCenterOffset);
    const horizontalVerdict = judgeRange({
      value: horizontal,
      max: maxHorizontal,
      warningMargin: 0.15,
      confidence,
      uncertainBelow,
    });
    const verticalVerdict = checkVertical
      ? judgeRange({
          value: vertical,
          max: maxVertical,
          warningMargin: 0.15,
          confidence,
          uncertainBelow,
        })
      : ({ status: 'pass' as const, inside: true });

    const status =
      horizontalVerdict.status === 'fail' || verticalVerdict.status === 'fail'
        ? 'fail'
        : horizontalVerdict.status === 'warning' || verticalVerdict.status === 'warning'
          ? 'warning'
          : horizontalVerdict.status === 'uncertain' || verticalVerdict.status === 'uncertain'
            ? 'uncertain'
            : 'pass';

    const eyeLineY = geometry.eyeLineFromTop;
    const shiftLine = {
      type: 'line' as const,
      coordinates: [0.5, roundTo(eyeLineY, 4), roundTo(geometry.faceCenterX, 4), roundTo(eyeLineY, 4)] as [
        number,
        number,
        number,
        number,
      ],
    };

    const frameGeometry: NonNullable<CheckResult['geometry']> = [
      shiftLine,
      { type: 'point', coordinates: [roundTo(geometry.faceCenterX, 4), roundTo(eyeLineY, 4)] },
      { type: 'point', coordinates: [0.5, roundTo(eyeLineY, 4)] },
      {
        type: 'rect',
        coordinates: [
          roundTo(geometry.headBox.x, 4),
          roundTo(geometry.headBox.y, 4),
          roundTo(geometry.headBox.width, 4),
          roundTo(geometry.headBox.height, 4),
        ],
      },
    ];

    const horizontalProblem = horizontalVerdict.status !== 'pass';

    const message =
      status === 'pass'
        ? 'Голова расположена по центру кадра.'
        : status === 'uncertain'
          ? 'Положение головы относительно центра определено неуверенно.'
          : horizontalProblem
            ? `Голова смещена ${direction(geometry.horizontalCenterOffset)} от центра на ${(horizontal * 100).toFixed(1).replace('.', ',')} % ширины кадра.`
            : `Голова смещена по вертикали на ${(vertical * 100).toFixed(1).replace('.', ',')} % высоты кадра.`;

    return plainResult({
      id: facePositionCheck.id,
      title: facePositionCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(horizontal, 4),
      max: maxHorizontal,
      unit: 'ratio',
      message,
      requirement: `Смещение головы от центра: не более ${(maxHorizontal * 100).toFixed(0)} % ширины кадра.`,
      fixability: 'fixable',
      geometry: frameGeometry,
      confidence: roundTo(confidence, 2),
      details: {
        horizontalCenterOffset: geometry.horizontalCenterOffset,
        verticalCenterOffset: geometry.verticalCenterOffset,
        maxHorizontalOffset: maxHorizontal,
        verticalCheckEnabled: checkVertical,
        maxVerticalOffset: maxVertical,
        displayValue: `${(horizontal * 100).toFixed(1).replace('.', ',')} %`,
      },
    });
  },
};
